import type { ExcursionsResponse, RejectionsResponse, StrategyKey } from '@et/shared';
import { useState } from 'react';
import { signed, tone } from '../lib/format';
import { useT } from '../lib/i18n';
import { useResource } from '../lib/use-resource';

/**
 * Two diagnostic cards: why the bot is not opening trades (rejection reasons, last 7 days)
 * and how good the closed trades were (how far each went up/down while open, and after).
 */

function rejectionLabel(reason: string, t: (s: string) => string): string {
  const labels: Record<string, string> = {
    engine_not_running: t('Engine not running'),
    circuit_breaker_tripped: t('Daily loss limit reached'),
    confidence_below_floor: t('Confidence too low'),
    symbol_not_in_universe: t('Coin not in the list'),
    symbol_vetoed: t('Coin blocked (delisting)'),
    max_concurrent_trades: t('All trade slots taken'),
    strategy_max_concurrent_trades: t('Strategy’s own slots taken'),
    symbol_already_held: t('Coin already held'),
    strategy_budget_exhausted: t('Strategy’s capital share used up'),
    cooldown_after_loss: t('Waiting after a loss on this coin'),
    order_too_small: t('Order below the exchange minimum'),
    no_price: t('No price available'),
    internal_error: t('Internal error'),
    'gate:rvol': t('Check failed: trading not busy enough'),
    'gate:priced_in': t('Check failed: price already moved'),
    'gate:btc_regime': t('Check failed: Bitcoin falling'),
    'gate:spread': t('Check failed: buying too expensive (spread)'),
  };
  return labels[reason] ?? reason;
}

export function RejectionsCard(): JSX.Element {
  const t = useT();
  const res = useResource<RejectionsResponse>('/signals/rejections?days=7');
  const rows = res.data?.rows ?? [];
  const byReason = new Map<string, { count: number; strategies: Map<StrategyKey, number> }>();
  for (const r of rows) {
    const g = byReason.get(r.reason) ?? { count: 0, strategies: new Map<StrategyKey, number>() };
    g.count += r.count;
    g.strategies.set(r.strategyKey, (g.strategies.get(r.strategyKey) ?? 0) + r.count);
    byReason.set(r.reason, g);
  }
  const sorted = [...byReason.entries()].sort((a, b) => b[1].count - a[1].count);

  return (
    <div className="card">
      <h2>{t('Why no trades? (7 days)')}</h2>
      <div className="muted" style={{ marginBottom: 8 }}>{t('Signals the bot found but did not trade, by reason.')}</div>
      {res.error ? <div className="neg">{res.error}</div> : sorted.length === 0 ? (
        <div className="empty">{t('No rejected signals in the last 7 days.')}</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>{t('Reason')}</th><th>{t('Strategy')}</th><th className="num">{t('Signals')}</th></tr></thead>
            <tbody>
              {sorted.map(([reason, g]) => (
                <tr key={reason}>
                  <td>{rejectionLabel(reason, t)}</td>
                  <td className="muted">{[...g.strategies.entries()].map(([k, n]) => `${t(k)} ${String(n)}`).join(' · ')}</td>
                  <td className="num"><b>{g.count}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const pct = (v: string | null): string => (v === null ? '—' : `${signed(v)}%`);

/** Loaded on demand: each trade may need a page of 5-minute candles from Binance. */
export function TradeQualityCard({ query }: { query: string }): JSX.Element {
  const t = useT();
  const path = `/reports/excursions?${query}`;
  const [requested, setRequested] = useState<string | null>(null);
  const res = useResource<ExcursionsResponse>(requested === path ? path : null);
  const rows = requested === path ? (res.data?.summary.byCloseReason ?? []) : [];

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>{t('Trade quality')}</h2>
        <span style={{ flex: 1 }} />
        <button className="small" disabled={requested === path && res.loading} onClick={() => { setRequested(path); }}>{t('Calculate')}</button>
      </div>
      <div className="muted" style={{ marginBottom: 8 }}>
        {t('For the latest 50 closed trades: how far the price rose (best) and fell (worst) while the trade was open, and where it was 2 hours after selling. A low “best” everywhere means bad buys; a high “best” on losing trades means the sell rule gave it back.')}
      </div>
      {requested !== path ? null : res.error ? <div className="neg">{res.error}</div> : res.loading ? <div className="muted">{t('Calculating…')}</div> : rows.length === 0 ? (
        <div className="empty">{t('No closed trades in this range.')}</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>{t('Reason')}</th><th className="num">{t('Trades')}</th><th className="num">{t('Avg result')}</th><th className="num">{t('Avg best')}</th><th className="num">{t('Avg worst')}</th><th className="num">{t('2 h after selling')}</th></tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.closeReason ?? 'none'}>
                  <td>{g.closeReason ? t(g.closeReason) : '—'}</td>
                  <td className="num">{g.trades}</td>
                  <td className={`num ${tone(g.avgPnlPct)}`}>{signed(g.avgPnlPct)}%</td>
                  <td className="num pos">{pct(g.avgMfePct)}</td>
                  <td className="num neg">{pct(g.avgMaePct)}</td>
                  <td className={`num ${g.avgPostExitPct === null ? '' : tone(g.avgPostExitPct)}`}>{pct(g.avgPostExitPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {requested === path && res.data && <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>{t('{n} trades', { n: res.data.trades.length })}</div>}
    </div>
  );
}
